import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useEffectiveTenantId } from '@/hooks/useEffectiveTenantId';
import { getErrorMessage } from '@/lib/error-utils';

export interface AIAgentSettings {
  id: string;
  tenant_id: string;
  agent_name: string;
  agent_type: string;
  agent_role: string;
  system_prompt: string;
  capabilities: unknown;
  model_id: string | null;
  is_active: boolean;
  priority_level: number;
  media_preferences: unknown;
  coordination_rules: unknown;
  agent_mode: 'manual' | 'suggestions' | 'auto';
  is_enabled: boolean;
  default_model_id: string | null;
  multimodal_model_id: string | null;
  document_model_id: string | null;
  media_routing: unknown;
  confidence_threshold: number;
  auto_respond_delay: number;
  max_response_length: number;
  handoff_keywords: string[];
  business_context: unknown;
  working_hours: unknown;
  model_name?: string;
  model_provider?: string;
  created_at: string;
  updated_at: string;
}

export function useAIAgentSettings(overrideTenantId?: string) {
  const { t } = useTranslation(['aiAssistant', 'common']);
  const [settings, setSettings] = useState<AIAgentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const effectiveTenantId = useEffectiveTenantId();
  
  // Use override tenant ID if provided, otherwise use effective tenant ID (which handles impersonation)
  const tenantId = overrideTenantId || effectiveTenantId;

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!tenantId) {
        throw new Error('No tenant ID available');
      }

      // Get primary agent's mode from tenant schema
      const { data: agentMode, error: modeError } = await supabase.rpc(
        'get_tenant_primary_agent_mode',
        { p_tenant_id: tenantId }
      );

      if (modeError) {
        console.error('Error fetching agent mode:', modeError);
        throw new Error('Failed to fetch agent mode');
      }

      // Create settings object with real data
      const realSettings: AIAgentSettings = {
        id: 'primary-agent',
        tenant_id: tenantId,
        agent_name: 'Primary Agent',
        agent_type: 'primary',
        agent_role: 'assistant',
        system_prompt: 'You are a helpful WhatsApp assistant.',
        capabilities: [],
        model_id: null,
        is_active: true,
        priority_level: 1,
        media_preferences: {},
        coordination_rules: {},
        agent_mode: (agentMode as 'manual' | 'suggestions' | 'auto') || 'manual',
        is_enabled: true,
        default_model_id: null,
        multimodal_model_id: null,
        document_model_id: null,
        media_routing: {},
        confidence_threshold: 0.7,
        auto_respond_delay: 2,
        max_response_length: 500,
        handoff_keywords: [],
        business_context: {},
        working_hours: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      setSettings(realSettings);
    } catch (err: unknown) {
      console.error('Error fetching AI agent settings:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const initializeSettings = async (tenantId: string) => {
    try {
      // Placeholder for initialization
      toast({
        title: t('agentSettings.initializedTitle'),
        description: t('agentSettings.initializedDesc'),
      });
    } catch (err: unknown) {
      console.error('Error initializing AI agent settings:', err);
      setError(getErrorMessage(err));
    }
  };

  const updateSettings = async (updatedSettings: Partial<AIAgentSettings>) => {
    try {
      console.log('🔧 Update Settings called:', { updatedSettings, tenantId, settings });
      if (!settings || !tenantId) {
        throw new Error('No settings or tenant ID available');
      }

      // If agent_mode is being updated, save to database
      if (updatedSettings.agent_mode) {
        console.log('🔄 Calling set_tenant_primary_agent_mode RPC:', { p_tenant_id: tenantId, p_mode: updatedSettings.agent_mode });
        const { data: success, error: updateError } = await supabase.rpc(
          'set_tenant_primary_agent_mode',
          { 
            p_tenant_id: tenantId,
            p_mode: updatedSettings.agent_mode
          }
        );

        console.log('🔄 RPC Response:', { success, updateError });

        if (updateError) {
          throw new Error(updateError.message);
        }

        if (!success) {
          throw new Error('Failed to update agent mode');
        }
      }

      // Update local state
      setSettings(prev => prev ? { ...prev, ...updatedSettings } : null);

      toast({
        title: t('agentSettings.updatedTitle'),
        description: t('agentSettings.updatedDesc'),
      });

      return true;
    } catch (err: unknown) {
      console.error('Error updating AI agent settings:', err);
      setError(getErrorMessage(err));
      toast({
        title: t('agentSettings.updateFailedTitle'),
        description: getErrorMessage(err),
        variant: "destructive",
      });
      return false;
    }
  };

  const toggleAIAgent = async (enabled: boolean) => {
    return updateSettings({ is_enabled: enabled });
  };

  useEffect(() => {
    if (tenantId) {
      fetchSettings();
    }
  }, [tenantId]);

  return {
    settings,
    loading,
    error,
    updateSettings,
    toggleAIAgent,
    refetch: fetchSettings
  };
}